import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTour } from '@reactour/tour';
import { useMediaQuery, useTheme } from '@mui/material';
import { useOnboarding } from 'contextProviders/OnboardingProvider';
import { getTourConfig } from './tourRegistry';
import { setActiveTourKey } from './activeTour';
import { setTourOutcome } from './tourOutcome';

// The only tour whose targets live inside the (mobile-first) CANpark record flow
// rather than the CANapps nav/sidebar chrome, so it's the only one that still
// works below the 'md' breakpoint, where that chrome collapses behind the
// hamburger. Every other tour is blocked on narrow screens.
export const MOBILE_SAFE_TOUR_KEY = 'canpark_issue_ticket';

// Thin wrapper over reactour: launches a registered tour by its tour_key. If the
// tour declares a startRoute, we navigate there first (its targets live on that
// page) and open after a short tick so the route has rendered. Logs a 'started'
// event; completion/skip is logged by OnboardingTourController on close.
export const useTourDriver = () => {
	const { setSteps, setCurrentStep, setIsOpen } = useTour();
	const { logEvent } = useOnboarding();
	const navigate = useNavigate();
	const location = useLocation();
	const theme = useTheme();
	// Matches SharedLayout: below 'md' the sidebar/nav collapses behind the burger.
	const isBelowMd = useMediaQuery(theme.breakpoints.down('md'));

	// Returns true if the tour actually launched, false if it was blocked (so
	// callers like the intro auto-start don't burn their one shot on a narrow
	// screen). startTour's identity changes when isBelowMd flips, so an effect
	// depending on it re-runs — and can retry — when the viewport crosses 'md'.
	const startTour = useCallback(
		(tourKey: string): boolean => {
			const config = getTourConfig(tourKey);
			if (!config?.steps?.length) return false;

			// On narrow screens only the mobile-safe record flow can anchor its
			// steps; the rest would render against hidden/collapsed chrome.
			if (isBelowMd && tourKey !== MOBILE_SAFE_TOUR_KEY) return false;

			setActiveTourKey(tourKey);
			setTourOutcome(null);
			logEvent({ tourKey, status: 'started' });

			setSteps?.(config.steps);
			setCurrentStep(0);

			if (config.startRoute && location.pathname !== config.startRoute) {
				navigate(config.startRoute);
				// Let the destination route mount before reactour anchors step 0.
				setTimeout(() => setIsOpen(true), 80);
			} else {
				setIsOpen(true);
			}
			return true;
		},
		[
			setSteps,
			setCurrentStep,
			setIsOpen,
			logEvent,
			navigate,
			location.pathname,
			isBelowMd,
		],
	);

	return { startTour };
};
